import { Fragment, memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  Group,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import type { ProjectRow, ProjectTagRow, TicketListItem, WorkspaceUserRow } from '@/types/api'
import {
  DataTable,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  dataTableClass,
} from '@/components/ui'
import {
  IconAntennaBars4,
  IconChevronDown,
  IconChevronUp,
  IconEye,
  IconFile,
  IconFolder,
  IconMessage,
  IconMicrophone,
  IconPhoto,
  IconVideo,
} from '@/components/icons'
import { TicketListSubjectCell } from '@/components/tickets/TicketListSubjectCell'
import { PriorityLadder } from '@/components/tickets/PriorityLadder'
import { ProjectIcon } from '@/components/sidebar/ProjectIcon'
import {
  formatCreatedAtTableCell,
  formatTicketListUpdatedAt,
  formatWhen,
  lastUpdateSenderLabel,
  priorityLabel,
  statusLabel,
} from '@/lib/ticket-display'
import { classicStatusBadgeRootStyle } from '@/lib/classic-ticket-badges'
import { ticketListHighlightIcons } from '@/lib/ticket-list-highlight'
import { resolveUploadsUrl } from '@/lib/uploads'
import { resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import { useTheme } from '@/lib/theme-context'
import { ShiftPresenceDot } from '@/components/ui/ShiftPresenceDot'
import {
  TICKETS_LIST_COLUMN_ORDER,
  isTicketsListColumnVisible,
  ticketsListColumnWidthsPercent,
  type TicketsListColumnId,
  type TicketsListColumnVisibility,
} from '@/lib/tickets-list-columns'
import type { TicketsSortByKey, TicketsSortOrder } from '@/lib/tickets-list-sort'

/** Массовые действия на странице «Все обращения»: чекбоксы и «выбрать группу» */
export type TicketsListBulkSelectConfig = {
  selectedIds: ReadonlySet<string>
  /** Уникальный ключ группы (aria для «выбрать все в таблице») */
  stripKey: string
  onRowToggle: (ticketId: string, checked: boolean) => void
  onStripToggle: (ticketIdsInTable: string[], checked: boolean) => void
}

const LIST_AVATAR_PX = 16
const COL_USER_FZ = 12
const COL_USER_SECONDARY_FZ = 11

function listPriorityBadgeLabel(priority: string): string {
  const p = (priority || '').toLowerCase()
  if (p === 'urgent') return 'Срочный'
  return priorityLabel(priority)
}

function useTicketsListAvatarPx() {
  const theme = useMantineTheme()
  const narrow = useMediaQuery('(max-width: 36em)') ?? false
  const isLgUp = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`) ?? false
  if (narrow) return 14
  if (!isLgUp) return 15
  return LIST_AVATAR_PX
}

/** Узкая таблица: планшет и ниже относительно `theme.breakpoints.lg` (1280px). */
function useTicketsListSqueeze1024() {
  const theme = useMantineTheme()
  const isLgUp = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`) ?? false
  return !isLgUp
}

function userLine(u: WorkspaceUserRow | undefined): string {
  if (!u) return '—'
  const parts = [u.first_name, u.last_name].filter(Boolean) as string[]
  if (parts.length) return parts.join(' ')
  return u.email || '—'
}

function assigneeInitials(u: WorkspaceUserRow | undefined): string {
  if (!u) return 'U'
  const a = u.first_name?.trim()?.[0]
  const b = u.last_name?.trim()?.[0]
  const c = u.email?.trim()?.[0]
  return ((a || b || c || 'U') + '').toUpperCase()
}

function AssigneeCell({
  t,
  usersById,
}: {
  t: TicketListItem
  usersById: Map<string, WorkspaceUserRow>
}) {
  const [broken, setBroken] = useState(false)
  const avatarPx = useTicketsListAvatarPx()
  const squeeze = useTicketsListSqueeze1024()
  const fzUser = squeeze ? 12 : { base: 11, sm: COL_USER_FZ }
  const fzDeleg = squeeze ? 11 : { base: 10, sm: COL_USER_SECONDARY_FZ }
  const rowGap = squeeze ? 2 : 4

  if (!t.assigned_to_id) {
    return (
      <Text fz={fzUser} c="dimmed" lh={1.3}>
        Не назначен
      </Text>
    )
  }
  const u = usersById.get(t.assigned_to_id)
  const name = userLine(u)
  const src =
    u && !broken
      ? (resolveOperatorAvatarSrc({
          operator_name: userLine(u),
          operator_email: u.email,
          avatar_url: u.avatar_url,
        }) || '')
      : ''

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Group gap={rowGap} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
        <ShiftPresenceDot userId={t.assigned_to_id} size={6} />
        <Avatar
          src={src || undefined}
          size={avatarPx}
          radius="xl"
          color="brand"
          variant="light"
          style={{ flexShrink: 0 }}
          onError={() => setBroken(true)}
        >
          {!src ? assigneeInitials(u) : null}
        </Avatar>
        <Text fz={fzUser} lh={1.3} truncate title={name} style={{ flex: 1, minWidth: 0 }}>
          {name}
        </Text>
      </Group>
      {t.delegated_from_operator_name || t.delegated_to_operator_name ? (
        <Text
          fz={fzDeleg}
          c="dimmed"
          truncate
          title={
            t.delegated_to_operator_name
              ? `Временно у: ${t.delegated_to_operator_name}${
                  t.delegated_from_operator_name ? ` · от: ${t.delegated_from_operator_name}` : ''
                }`
              : `Дополнительно от: ${t.delegated_from_operator_name}`
          }
          lh={1.25}
        >
          {t.delegated_to_operator_name
            ? `Временно у: ${t.delegated_to_operator_name}`
            : `Дополнительно от: ${t.delegated_from_operator_name}`}
        </Text>
      ) : null}
    </Stack>
  )
}

function CustomerCell({ t }: { t: TicketListItem }) {
  const [broken, setBroken] = useState(false)
  const avatarPx = useTicketsListAvatarPx()
  const squeeze = useTicketsListSqueeze1024()
  const fzUser = squeeze ? 12 : { base: 11, sm: COL_USER_FZ }
  const fzSec = squeeze ? 11 : { base: 10, sm: COL_USER_SECONDARY_FZ }
  const rowGap = squeeze ? 2 : 4
  const name = t.customer_name || t.customer_email || '—'
  const initial = (
    t.customer_name?.trim()?.[0] ||
    t.customer_email?.trim()?.[0] ||
    '?'
  ).toUpperCase()
  const src =
    t.customer_avatar_url && !broken ? resolveUploadsUrl(t.customer_avatar_url) : ''

  const img = t.attachment_images ?? 0
  const vid = t.attachment_videos ?? 0
  const aud = t.attachment_audio ?? 0
  const oth = t.attachment_other ?? 0
  const hasAttachments = img > 0 || vid > 0 || aud > 0 || oth > 0

  const indent = avatarPx + 4

  return (
    <Stack gap={squeeze ? 2 : 3} style={{ minWidth: 0 }}>
      <Group gap={rowGap} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
        <Avatar
          src={src || undefined}
          size={avatarPx}
          radius="xl"
          color="gray"
          variant="light"
          style={{ flexShrink: 0 }}
          onError={() => setBroken(true)}
        >
          {!src ? initial : null}
        </Avatar>
        <Text fz={fzUser} lh={1.3} truncate title={name} style={{ flex: 1, minWidth: 0 }}>
          {name}
        </Text>
      </Group>
      {hasAttachments ? (
        <Group
          gap={squeeze ? 4 : 6}
          wrap="nowrap"
          style={{ paddingLeft: indent, minWidth: 0 }}
          title={[
            img ? `Картинок: ${img}` : '',
            vid ? `Видео: ${vid}` : '',
            aud ? `Аудио: ${aud}` : '',
            oth ? `Прочих: ${oth}` : '',
          ]
            .filter(Boolean)
            .join(', ')}
        >
          {img > 0 ? (
            <Tooltip label={`Картинок: ${img}`} withArrow>
              <Group gap={2} wrap="nowrap" style={{ cursor: 'default' }}>
                <IconPhoto size={10} stroke={1.5} style={{ opacity: 0.65 }} />
                <Text component="span" fz={fzSec} fw={500} lh={1}>
                  {img}
                </Text>
              </Group>
            </Tooltip>
          ) : null}
          {vid > 0 ? (
            <Tooltip label={`Видео: ${vid}`} withArrow>
              <Group gap={2} wrap="nowrap" style={{ cursor: 'default' }}>
                <IconVideo size={10} stroke={1.5} style={{ opacity: 0.65 }} />
                <Text component="span" fz={fzSec} fw={500} lh={1}>
                  {vid}
                </Text>
              </Group>
            </Tooltip>
          ) : null}
          {aud > 0 ? (
            <Tooltip label={`Аудио: ${aud}`} withArrow>
              <Group gap={2} wrap="nowrap" style={{ cursor: 'default' }}>
                <IconMicrophone size={10} stroke={1.5} style={{ opacity: 0.65 }} />
                <Text component="span" fz={fzSec} fw={500} lh={1}>
                  {aud}
                </Text>
              </Group>
            </Tooltip>
          ) : null}
          {oth > 0 ? (
            <Tooltip label={`Прочих файлов: ${oth}`} withArrow>
              <Group gap={2} wrap="nowrap" style={{ cursor: 'default' }}>
                <IconFile size={10} stroke={1.5} style={{ opacity: 0.65 }} />
                <Text component="span" fz={fzSec} fw={500} lh={1}>
                  {oth}
                </Text>
              </Group>
            </Tooltip>
          ) : null}
        </Group>
      ) : null}
    </Stack>
  )
}

function ProjectCell({
  t,
  projectsById,
}: {
  t: TicketListItem
  projectsById: Map<string, ProjectRow>
}) {
  const p = projectsById.get(t.project_id)
  if (p) {
    return (
      <Tooltip label={t.project_name || p.name} withArrow position="top">
        <Box style={{ display: 'flex', justifyContent: 'center' }}>
          <ProjectIcon project={p} boxSize={18} />
        </Box>
      </Tooltip>
    )
  }
  const letter = (t.project_name || '?').trim().slice(0, 1).toUpperCase() || '?'
  return (
    <Text size="sm" c="dimmed" ta="center" fw={600}>
      {letter}
    </Text>
  )
}

function SortableTh({
  sortKey,
  sortBy,
  sortOrder,
  onSort,
  children,
  align = 'left',
  title: thTitle,
  variantNum,
  variantNarrow,
  thAlign,
  thClassName,
}: {
  sortKey: TicketsSortByKey
  sortBy: string
  sortOrder: TicketsSortOrder
  onSort: (k: TicketsSortByKey) => void
  children: ReactNode
  align?: 'left' | 'center' | 'right'
  title?: string
  variantNum?: boolean
  variantNarrow?: boolean
  thAlign?: 'left' | 'center' | 'right'
  thClassName?: string
}) {
  const active = sortBy === sortKey
  const justify =
    align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
  const cellAlign = thAlign ?? (align === 'center' ? 'center' : align === 'right' ? 'right' : 'left')

  return (
    <DataTh
      align={cellAlign}
      title={thTitle}
      variantNum={variantNum}
      variantNarrow={variantNarrow}
      className={thClassName}
      aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <UnstyledButton
        type="button"
        className="tickets-sort-th__btn"
        onClick={() => onSort(sortKey)}
        aria-label={`Сортировать по: ${thTitle ?? sortKey}`}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: justify,
          gap: 4,
          minWidth: 0,
        }}
      >
        <Box component="span" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {children}
        </Box>
        {active ? (
          sortOrder === 'desc' ? (
            <IconChevronDown size={14} stroke={2} style={{ flexShrink: 0, opacity: 0.75 }} aria-hidden />
          ) : (
            <IconChevronUp size={14} stroke={2} style={{ flexShrink: 0, opacity: 0.75 }} aria-hidden />
          )
        ) : null}
      </UnstyledButton>
    </DataTh>
  )
}

function ViewersCell({ t }: { t: TicketListItem }) {
  const n = t.viewers?.length ?? 0
  if (n <= 0) return null
  const names = t.viewers?.map((v) => v.user_name).filter(Boolean).join('\n') ?? ''
  return (
    <Tooltip label={<span style={{ whiteSpace: 'pre-line' }}>{names}</span>} disabled={!names} withArrow>
      <Group gap={3} justify="center" wrap="nowrap" align="center" style={{ cursor: 'default' }}>
        <IconEye size={14} stroke={1.5} style={{ opacity: 0.55 }} />
        <Text fz={12} lh={1.2} fw={500} component="span">
          {n}
        </Text>
      </Group>
    </Tooltip>
  )
}

function renderTicketsColumnTh(
  columnId: TicketsListColumnId,
  p: {
    sortBy: string
    sortOrder: TicketsSortOrder
    onSort: (k: TicketsSortByKey) => void
  },
): ReactNode {
  const { sortBy, sortOrder, onSort } = p
  switch (columnId) {
    case 'status':
      return (
        <SortableTh sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} title="Статус">
          Статус
        </SortableTh>
      )
    case 'viewers':
      return (
        <SortableTh
          sortKey="viewers"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          align="center"
          title="Просматривают"
        >
          <IconEye size={18} stroke={1.2} style={{ display: 'block', margin: '0 auto', opacity: 0.7 }} />
        </SortableTh>
      )
    case 'priority':
      return (
        <SortableTh
          sortKey="priority"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          align="center"
          title="Приоритет"
        >
          <IconAntennaBars4
            size={18}
            stroke={1.2}
            style={{ display: 'block', margin: '0 auto', opacity: 0.65 }}
          />
        </SortableTh>
      )
    case 'weight':
      return (
        <DataTh align="center" variantNarrow title="Вес обращения (метки + канал + проект)">
          Вес
        </DataTh>
      )
    case 'project':
      return (
        <SortableTh
          sortKey="project"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          align="center"
          title="Проект"
        >
          <IconFolder size={18} stroke={1.2} style={{ display: 'block', margin: '0 auto', opacity: 0.7 }} />
        </SortableTh>
      )
    case 'ticket_number':
      return (
        <SortableTh
          sortKey="ticket_number"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          variantNum
          thClassName="tickets-th--ticket-num"
          title="Номер обращения"
        >
          #
        </SortableTh>
      )
    case 'subject':
      return (
        <SortableTh sortKey="subject" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} title="Тема">
          Тема
        </SortableTh>
      )
    case 'created_at':
      return (
        <SortableTh
          sortKey="created_at"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          title="Создан"
          thClassName="tickets-th--created"
        >
          Создан
        </SortableTh>
      )
    case 'updated_at':
      return (
        <SortableTh
          sortKey="updated_at"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          title="Дата и автор последнего обновления"
          thClassName="tickets-th--updated"
        >
          Обновлён
        </SortableTh>
      )
    case 'assigned':
      return (
        <SortableTh
          sortKey="assigned"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          title="Назначен"
          thClassName="tickets-th--assignee"
        >
          Назначен
        </SortableTh>
      )
    case 'customer':
      return (
        <SortableTh
          sortKey="customer"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          title="Пользователь"
          thClassName="tickets-th--customer"
        >
          Пользователь
        </SortableTh>
      )
    case 'channel':
      return (
        <SortableTh sortKey="channel" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} title="Канал">
          Канал
        </SortableTh>
      )
    case 'messages':
      return (
        <SortableTh
          sortKey="messages"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={onSort}
          variantNarrow
          align="center"
          title="Сообщений"
        >
          <IconMessage
            size={18}
            stroke={1.2}
            style={{ display: 'block', margin: '0 auto', opacity: 0.68 }}
            aria-hidden
          />
        </SortableTh>
      )
    default: {
      const _exhaustive: never = columnId
      return _exhaustive
    }
  }
}

function renderTicketsColumnTd(
  columnId: TicketsListColumnId,
  t: TicketListItem,
  ctx: {
    usersById: Map<string, WorkspaceUserRow>
    projectsById: Map<string, ProjectRow>
    tagsByProjectId: Map<string, ProjectTagRow[]>
    squeeze: boolean
    classicBadgeDark: boolean
  },
): ReactNode {
  const { usersById, projectsById, tagsByProjectId, squeeze, classicBadgeDark } = ctx
  const dateFz = squeeze ? 11 : 12
  switch (columnId) {
    case 'status': {
      const project = projectsById.get(t.project_id)
      const highlights = ticketListHighlightIcons(t, project)
      return (
        <DataTd>
          <Group gap={6} wrap="nowrap" align="center">
            {highlights.length > 0 ? (
              <Group gap={2} wrap="nowrap" style={{ flexShrink: 0 }}>
                {highlights.map((h, i) => (
                  <Tooltip key={`${h.kind}-${i}`} label={h.title} withArrow position="top">
                    <Text
                      component="span"
                      fz={14}
                      lh={1}
                      role="img"
                      aria-label={h.title}
                      style={{
                        color: h.kind === 'fire' ? '#ef4444' : '#f59e0b',
                        cursor: 'default',
                        lineHeight: 1,
                      }}
                    >
                      {h.kind === 'fire' ? '🔥' : '⏰'}
                    </Text>
                  </Tooltip>
                ))}
              </Group>
            ) : null}
            <Badge
              size="sm"
              variant="light"
              radius="sm"
              tt="none"
              fw={600}
              color="gray"
              styles={{
                root: {
                  ...classicStatusBadgeRootStyle(t.status, classicBadgeDark),
                  letterSpacing: '0.3px',
                  border: 'none',
                },
              }}
            >
              {statusLabel(t.status)}
            </Badge>
          </Group>
        </DataTd>
      )
    }
    case 'viewers':
      return (
        <DataTd align="center" narrow>
          <ViewersCell t={t} />
        </DataTd>
      )
    case 'priority':
      return (
        <DataTd align="center" narrow>
          <Tooltip label={listPriorityBadgeLabel(t.priority)} withArrow position="top">
            <Box style={{ display: 'flex', justifyContent: 'center' }}>
              <PriorityLadder priority={t.priority} />
            </Box>
          </Tooltip>
        </DataTd>
      )
    case 'weight':
      return (
        <DataTd align="center" narrow mono num>
          {Number(t.weight) || 0}
        </DataTd>
      )
    case 'project':
      return (
        <DataTd narrow>
          <ProjectCell t={t} projectsById={projectsById} />
        </DataTd>
      )
    case 'ticket_number':
      return (
        <DataTd mono num className="tickets-td--ticket-num">
          {t.ticket_number}
        </DataTd>
      )
    case 'subject':
      return (
        <DataTd subject>
          <TicketListSubjectCell
            ticket={t}
            subject={t.subject_translated || t.subject}
            tagIds={t.tag_ids}
            projectTags={tagsByProjectId.get(t.project_id) ?? []}
          />
        </DataTd>
      )
    case 'created_at':
      return (
        <DataTd muted title={formatWhen(t.created_at)} className="tickets-td--created">
          <Text
            fz={dateFz}
            lh={1.2}
            className={dataTableClass.mono}
            style={{ whiteSpace: 'nowrap' }}
          >
            {formatCreatedAtTableCell(t.created_at)}
          </Text>
        </DataTd>
      )
    case 'updated_at':
      return (
        <DataTd
          muted
          clip
          className="tickets-td--updated"
          title={`${formatWhen(t.last_update_at || t.updated_at || t.created_at)} · ${lastUpdateSenderLabel(t)}`}
        >
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text
              fz={dateFz}
              lh={1.2}
              className={dataTableClass.mono}
              truncate
              style={{ whiteSpace: 'nowrap' }}
            >
              {formatTicketListUpdatedAt(t)}
            </Text>
            <Text fz={dateFz} lh={1.2} truncate className="tickets-updated-author">
              {lastUpdateSenderLabel(t)}
            </Text>
          </Stack>
        </DataTd>
      )
    case 'assigned':
      return (
        <DataTd clip className={dataTableClass.ticketsTdAssignee}>
          <AssigneeCell t={t} usersById={usersById} />
        </DataTd>
      )
    case 'customer':
      return (
        <DataTd clip className={dataTableClass.ticketsTdCustomer}>
          <CustomerCell t={t} />
        </DataTd>
      )
    case 'channel':
      return (
        <DataTd muted>
          <Text fz={{ base: 11, sm: COL_USER_FZ }} lh={1.3} truncate>
            {t.channel_name || '—'}
          </Text>
        </DataTd>
      )
    case 'messages':
      return (
        <DataTd mono narrow align="center">
          {t.messages_count}
        </DataTd>
      )
    default: {
      const _exhaustive: never = columnId
      return _exhaustive
    }
  }
}

function visibleTicketsListColumnIds(visibility: TicketsListColumnVisibility): TicketsListColumnId[] {
  return TICKETS_LIST_COLUMN_ORDER.filter((id) => isTicketsListColumnVisible(id, visibility))
}

function BulkSelectTh({
  bulkSelect,
  stripRowIds,
}: {
  bulkSelect: TicketsListBulkSelectConfig
  stripRowIds: string[]
}) {
  const nSel = stripRowIds.filter((id) => bulkSelect.selectedIds.has(id)).length
  const allChecked = stripRowIds.length > 0 && nSel === stripRowIds.length
  const indeterminate = nSel > 0 && nSel < stripRowIds.length

  return (
    <DataTh
      align="center"
      variantNarrow
      className="tickets-th--select"
      style={{ width: 38, verticalAlign: 'middle' }}
    >
      <Checkbox
        size="xs"
        aria-label={`Выбрать все обращения в группе (${bulkSelect.stripKey})`}
        checked={allChecked}
        indeterminate={indeterminate}
        onChange={() => {
          // Не брать checked из события: в состоянии indeterminate оно часто неверное;
          // второй клик при «все выбраны» должен снимать выделение в группе.
          bulkSelect.onStripToggle(stripRowIds, !allChecked)
        }}
      />
    </DataTh>
  )
}

export function TicketsListThead({
  columnVisibility,
  sortBy,
  sortOrder,
  onSort,
  bulkSelect,
  stripRowIds,
}: {
  columnVisibility: TicketsListColumnVisibility
  sortBy: string
  sortOrder: TicketsSortOrder
  onSort: (k: TicketsSortByKey) => void
  bulkSelect?: TicketsListBulkSelectConfig | null
  stripRowIds?: string[]
}) {
  const ids = visibleTicketsListColumnIds(columnVisibility)
  const rowIds = stripRowIds ?? []
  return (
    <DataThead>
      <DataTr>
        {bulkSelect && rowIds.length > 0 ? (
          <BulkSelectTh bulkSelect={bulkSelect} stripRowIds={rowIds} />
        ) : null}
        {ids.map((id) => (
          <Fragment key={id}>{renderTicketsColumnTh(id, { sortBy, sortOrder, onSort })}</Fragment>
        ))}
      </DataTr>
    </DataThead>
  )
}

const TicketsListRow = memo(function TicketsListRow({
  t,
  columnVisibility,
  usersById,
  projectsById,
  tagsByProjectId,
  bulkSelect,
  onRowClick,
  squeeze,
  classicBadgeDark,
}: {
  t: TicketListItem
  columnVisibility: TicketsListColumnVisibility
  usersById: Map<string, WorkspaceUserRow>
  projectsById: Map<string, ProjectRow>
  tagsByProjectId: Map<string, ProjectTagRow[]>
  bulkSelect?: TicketsListBulkSelectConfig | null
  onRowClick?: (ticket: TicketListItem) => void
  squeeze: boolean
  classicBadgeDark: boolean
}) {
  const ids = visibleTicketsListColumnIds(columnVisibility)
  return (
    <DataTr
      className="tickets-list-row"
      data-ticket-status={t.status}
      style={onRowClick ? { cursor: 'pointer' } : undefined}
      onClick={() => onRowClick?.(t)}
    >
      {bulkSelect ? (
        <DataTd
          narrow
          align="center"
          className="tickets-td--select"
          style={{ width: 38, verticalAlign: 'middle' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="xs"
            checked={bulkSelect.selectedIds.has(t.id)}
            onChange={(e) => bulkSelect.onRowToggle(t.id, e.currentTarget.checked)}
            aria-label={`Выбрать обращение №${t.ticket_number}`}
          />
        </DataTd>
      ) : null}
      {ids.map((id) => (
        <Fragment key={id}>
          {renderTicketsColumnTd(id, t, {
            usersById,
            projectsById,
            tagsByProjectId,
            squeeze,
            classicBadgeDark,
          })}
        </Fragment>
      ))}
    </DataTr>
  )
})

export function TicketsListTableBody({
  columnVisibility,
  rows,
  usersById,
  projectsById,
  tagsByProjectId,
  bulkSelect,
  onRowClick,
  squeeze = false,
  topPaddingPx = 0,
  bottomPaddingPx = 0,
}: {
  columnVisibility: TicketsListColumnVisibility
  rows: TicketListItem[]
  usersById: Map<string, WorkspaceUserRow>
  projectsById: Map<string, ProjectRow>
  tagsByProjectId: Map<string, ProjectTagRow[]>
  bulkSelect?: TicketsListBulkSelectConfig | null
  onRowClick?: (ticket: TicketListItem) => void
  /** Узкий viewport (ниже `lg`): чуть мельче даты в колонках */
  squeeze?: boolean
  topPaddingPx?: number
  bottomPaddingPx?: number
}) {
  const { theme } = useTheme()
  const classicBadgeDark = theme === 'dark'
  const ids = visibleTicketsListColumnIds(columnVisibility)
  const columnCount = ids.length + (bulkSelect ? 1 : 0)
  return (
    <DataTbody>
      {topPaddingPx > 0 ? (
        <DataTr aria-hidden>
          <DataTd colSpan={columnCount} style={{ height: topPaddingPx, padding: 0, border: 0 }} />
        </DataTr>
      ) : null}
      {rows.map((t) => (
        <TicketsListRow
          key={t.id}
          t={t}
          columnVisibility={columnVisibility}
          usersById={usersById}
          projectsById={projectsById}
          tagsByProjectId={tagsByProjectId}
          bulkSelect={bulkSelect}
          onRowClick={onRowClick}
          squeeze={squeeze}
          classicBadgeDark={classicBadgeDark}
        />
      ))}
      {bottomPaddingPx > 0 ? (
        <DataTr aria-hidden>
          <DataTd colSpan={columnCount} style={{ height: bottomPaddingPx, padding: 0, border: 0 }} />
        </DataTr>
      ) : null}
    </DataTbody>
  )
}

export function TicketsListTable({
  rows,
  usersById,
  projectsById,
  tagsByProjectId,
  sortBy,
  sortOrder,
  onSort,
  columnVisibility,
  bulkSelect,
  onRowClick,
}: {
  rows: TicketListItem[]
  usersById: Map<string, WorkspaceUserRow>
  projectsById: Map<string, ProjectRow>
  tagsByProjectId: Map<string, ProjectTagRow[]>
  sortBy: string
  sortOrder: TicketsSortOrder
  onSort: (k: TicketsSortByKey) => void
  columnVisibility: TicketsListColumnVisibility
  bulkSelect?: TicketsListBulkSelectConfig | null
  onRowClick?: (ticket: TicketListItem) => void
}) {
  const widthCols = useMemo(() => ticketsListColumnWidthsPercent(columnVisibility), [columnVisibility])
  const stripRowIds = useMemo(() => rows.map((r) => r.id), [rows])
  const theme = useMantineTheme()
  const squeeze = !(useMediaQuery(`(min-width: ${theme.breakpoints.lg})`) ?? false)
  const tableRef = useRef<HTMLTableElement | null>(null)
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: rows.length })

  const VIRTUALIZE_MIN_ROWS = 80
  /** Стартовая оценка до первого замера; дальше берётся средняя реальная высота отрисованных строк. */
  const ROW_HEIGHT_INITIAL = 36
  /** Запас рендера выше/ниже видимой области в высотах viewport — чтобы быстрый скролл не упирался в пустой спейсер. */
  const OVERSCAN_VIEWPORTS = 1.5
  const MIN_OVERSCAN_ROWS = 10
  const virtualizationEnabled = rows.length >= VIRTUALIZE_MIN_ROWS
  const [rowHeight, setRowHeight] = useState(ROW_HEIGHT_INITIAL)

  useEffect(() => {
    if (!virtualizationEnabled) {
      setVisibleRange({ start: 0, end: rows.length })
      return
    }

    // Страница скроллится не окном, а ближайшим прокручиваемым предком (AppShell.Main и т.п.)
    const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      for (let n = el?.parentElement ?? null; n; n = n.parentElement) {
        const oy = getComputedStyle(n).overflowY
        if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n
      }
      return null
    }

    // Контейнер ищем один раз (getComputedStyle по предкам дорогой для каждого кадра скролла); при resize — заново
    let parent = findScrollParent(tableRef.current)
    let rafId = 0
    const updateRange = () => {
      const tableEl = tableRef.current
      if (!tableEl) return
      const parentRect = parent ? parent.getBoundingClientRect() : null
      const viewportTop = parentRect ? parentRect.top : 0
      const viewportHeight = parentRect ? parentRect.height : window.innerHeight || 0
      const tableTop = tableEl.getBoundingClientRect().top
      const headHeight = tableEl.tHead?.offsetHeight ?? 0

      const offsetTop = viewportTop - tableTop - headHeight
      const overscanPx = viewportHeight * OVERSCAN_VIEWPORTS
      const first = Math.floor((offsetTop - overscanPx) / rowHeight)
      const last = Math.ceil((offsetTop + viewportHeight + overscanPx) / rowHeight)
      let start = Math.max(0, first)
      let end = Math.min(rows.length, Math.max(last, start + MIN_OVERSCAN_ROWS))
      start = Math.min(start, Math.max(0, end - MIN_OVERSCAN_ROWS))

      setVisibleRange((prev) => {
        // Не перерисовываем, пока новое окно помещается в уже отрисованное с запасом — меньше коммитов при скролле.
        if (prev.start <= start && prev.end >= end && prev.start - start > -MIN_OVERSCAN_ROWS && prev.end - end < MIN_OVERSCAN_ROWS * 4) {
          return prev
        }
        return { start, end }
      })
    }

    const scheduleUpdate = () => {
      if (rafId) return
      rafId = window.requestAnimationFrame(() => {
        rafId = 0
        updateRange()
      })
    }

    updateRange()
    // capture: scroll не всплывает, а контейнер прокрутки вложенный (AppShell.Main — overflow: hidden)
    window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true })
    const onResize = () => {
      parent = findScrollParent(tableRef.current)
      scheduleUpdate()
    }
    window.addEventListener('resize', onResize)
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', scheduleUpdate, { capture: true })
      window.removeEventListener('resize', onResize)
    }
  }, [rows.length, virtualizationEnabled, rowHeight])

  const virtualRows = useMemo(() => {
    if (!virtualizationEnabled) return rows
    return rows.slice(visibleRange.start, visibleRange.end)
  }, [rows, visibleRange.start, visibleRange.end, virtualizationEnabled])

  // Реальная высота строки: спейсеры считаются по ней, иначе позиция/общая высота таблицы расходятся с оценкой.
  useLayoutEffect(() => {
    if (!virtualizationEnabled) return
    const trs = tableRef.current?.querySelectorAll<HTMLTableRowElement>('tbody > tr.tickets-list-row')
    if (!trs || trs.length === 0) return
    let sum = 0
    trs.forEach((tr) => {
      sum += tr.offsetHeight
    })
    const avg = sum / trs.length
    if (avg > 0) setRowHeight((prev) => (Math.abs(prev - avg) < 1 ? prev : avg))
  }, [virtualRows, virtualizationEnabled])

  const topPaddingPx = virtualizationEnabled ? visibleRange.start * rowHeight : 0
  const bottomPaddingPx = virtualizationEnabled
    ? Math.max(0, (rows.length - visibleRange.end) * rowHeight)
    : 0

  if (rows.length === 0) return null
  return (
    <DataTable ref={tableRef} className={dataTableClass.ticketsList}>
      <colgroup>
        {bulkSelect ? <col key="__select" className="tickets-col-select" style={{ width: 38 }} /> : null}
        {widthCols.map(({ id, widthPct }) => (
          <col key={id} style={{ width: widthPct }} />
        ))}
      </colgroup>
      <TicketsListThead
        columnVisibility={columnVisibility}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={onSort}
        bulkSelect={bulkSelect}
        stripRowIds={stripRowIds}
      />
      <TicketsListTableBody
        columnVisibility={columnVisibility}
        rows={virtualRows}
        usersById={usersById}
        projectsById={projectsById}
        tagsByProjectId={tagsByProjectId}
        bulkSelect={bulkSelect}
        onRowClick={onRowClick}
        squeeze={squeeze}
        topPaddingPx={topPaddingPx}
        bottomPaddingPx={bottomPaddingPx}
      />
    </DataTable>
  )
}
